import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import gsap from "gsap";

function buildShield() {
  const group = new THREE.Group();
  const shape = new THREE.Shape();
  shape.moveTo(0, 1.15);
  shape.quadraticCurveTo(0.85, 1.02, 0.95, 0.45);
  shape.quadraticCurveTo(0.98, -0.4, 0, -1.15);
  shape.quadraticCurveTo(-0.98, -0.4, -0.95, 0.45);
  shape.quadraticCurveTo(-0.85, 1.02, 0, 1.15);
  const geo = new THREE.ExtrudeGeometry(shape, {
    depth: 0.22, bevelEnabled: true, bevelThickness: 0.06, bevelSize: 0.05, bevelSegments: 3, curveSegments: 24,
  });
  const gold = new THREE.MeshStandardMaterial({ color: "#FFFFFF", metalness: 0.85, roughness: 0.28 });
  group.add(new THREE.Mesh(geo, gold));

  const canvas = document.createElement("canvas");
  canvas.width = canvas.height = 256;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#0E2A54";
  ctx.font = "900 190px Georgia, serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("$", 128, 138);
  const tex = new THREE.CanvasTexture(canvas);
  const sign = new THREE.Mesh(
    new THREE.PlaneGeometry(1.15, 1.15),
    new THREE.MeshBasicMaterial({ map: tex, transparent: true })
  );
  sign.position.z = 0.35;
  group.add(sign);
  return group;
}

export const ShieldPanel = ({ children }) => {
  const wrapRef = useRef(null);
  const canvasWrapRef = useRef(null);
  const stateRef = useRef({ active: false });
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    setIsDesktop(window.matchMedia("(pointer: fine) and (min-width: 768px)").matches);
  }, []);

  useEffect(() => {
    if (!isDesktop) return;
    const wrap = wrapRef.current;
    const cWrap = canvasWrapRef.current;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, 4 / 5, 0.1, 50);
    camera.position.z = 4.2;
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    const size = () => {
      const r = wrap.getBoundingClientRect();
      renderer.setSize(r.width, r.height);
      camera.aspect = r.width / r.height;
      camera.updateProjectionMatrix();
    };
    size();
    cWrap.appendChild(renderer.domElement);

    scene.add(new THREE.AmbientLight(0xffffff, 0.45));
    const key = new THREE.DirectionalLight(0xeaf2ff, 1.6);
    key.position.set(2, 3, 4);
    scene.add(key);
    const rim = new THREE.PointLight(0xffffff, 2.2, 12);
    rim.position.set(0, -1, 2.5);
    scene.add(rim);

    const shield = buildShield();
    shield.position.y = -3.2;
    scene.add(shield);

    const st = stateRef.current;
    st.tiltX = 0; st.tiltY = 0;
    let raf;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      shield.rotation.x += (st.tiltX - shield.rotation.x) * 0.08;
      shield.rotation.y += (st.tiltY - shield.rotation.y) * 0.08;
      renderer.render(scene, camera);
    };
    tick();

    const enter = () => {
      st.active = true;
      gsap.to(shield.position, { y: 0, duration: 0.6, ease: "power3.out" });
      gsap.to(cWrap, { opacity: 1, duration: 0.35 });
    };
    const leave = () => {
      st.active = false;
      st.tiltX = 0; st.tiltY = 0;
      gsap.to(shield.position, { y: -3.2, duration: 0.55, ease: "power3.in" });
      gsap.to(cWrap, { opacity: 0, duration: 0.45, delay: 0.15 });
    };
    const move = (e) => {
      if (!st.active) return;
      const r = wrap.getBoundingClientRect();
      const nx = (e.clientX - r.left) / r.width - 0.5;
      const ny = (e.clientY - r.top) / r.height - 0.5;
      st.tiltY = nx * 0.52;
      st.tiltX = ny * 0.52;
    };
    wrap.addEventListener("mouseenter", enter);
    wrap.addEventListener("mouseleave", leave);
    wrap.addEventListener("mousemove", move);
    window.addEventListener("resize", size);

    return () => {
      cancelAnimationFrame(raf);
      wrap.removeEventListener("mouseenter", enter);
      wrap.removeEventListener("mouseleave", leave);
      wrap.removeEventListener("mousemove", move);
      window.removeEventListener("resize", size);
      renderer.dispose();
      cWrap.removeChild(renderer.domElement);
    };
  }, [isDesktop]);

  return (
    <div
      ref={wrapRef}
      data-testid="host-shield-panel"
      className={`relative overflow-hidden bg-[#0B234C] border border-white/10 ${!isDesktop ? "pulse-border" : ""}`}
      style={{ aspectRatio: "4/5" }}
    >
      {["top-3 left-3 border-t border-l", "top-3 right-3 border-t border-r", "bottom-3 left-3 border-b border-l", "bottom-3 right-3 border-b border-r"].map((c) => (
        <span key={c} className={`absolute w-5 h-5 border-[#FFFFFF] ${c}`} />
      ))}
      {children}
      {isDesktop && (
        <div ref={canvasWrapRef} className="absolute inset-0 opacity-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 55%, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.08) 40%, transparent 70%)" }} />
      )}
      <div className="absolute bottom-5 left-0 right-0 text-center font-mono text-[10px] tracking-[0.3em] text-white/30 pointer-events-none">
        {isDesktop ? "[ HOVER TO REVEAL ]" : "[ PROTECTION + WEALTH ]"}
      </div>
    </div>
  );
};
